// Browser-safe analytics contract: one typed dataLayer event, strict allowlists, URL sanitising and dedup.
// No DOM or network access here; side effects live in analytics-runtime.ts.

export const ANALYTICS_EVENT = "seoevo_analytics";
export const ANALYTICS_SCHEMA_VERSION = 1;
export const SITE_ORIGIN = "https://agenciaseoevolution.com";
export const SITE_TITLE = "SEOEVOLUTION";

export const EVENT_NAMES = [
  "page_view",
  "cta_click",
  "contact_click",
  "form_start",
  "generate_lead",
  "appointment_booked",
  "newsletter_subscribe",
  "view_case_study",
] as const;
export const BUSINESS_LINES = ["agency", "website_rental"] as const;
export const SERVICES = ["google_maps", "ai_visibility", "web", "other", "unknown"] as const;
export const PAGE_TYPES = [
  "home",
  "service",
  "landing",
  "knowledge_index",
  "article",
  "case_studies",
  "contact",
  "company",
  "faq",
  "legal",
  "other",
] as const;
export const FORM_IDS = ["diagnosis_form", "newsletter_form"] as const;
export const CTA_LOCATIONS = [
  "header",
  "footer",
  "floating_whatsapp",
  "body",
  "diagnosis_form_auto_whatsapp",
  "calendly_embed",
  "case_studies",
] as const;
export const DESTINATION_TYPES = ["whatsapp", "phone", "email", "calendly", "diagnosis"] as const;
export const CASE_IDS = ["herrolex", "la-sagra", "itv-maquinas-de-hielo", "barbers-roldan"] as const;

export type EventName = (typeof EVENT_NAMES)[number];
export type BusinessLine = (typeof BUSINESS_LINES)[number];
export type Service = (typeof SERVICES)[number];
export type PageType = (typeof PAGE_TYPES)[number];
export type FormId = (typeof FORM_IDS)[number];
export type CtaLocation = (typeof CTA_LOCATIONS)[number];
export type DestinationType = (typeof DESTINATION_TYPES)[number];
export type CaseId = (typeof CASE_IDS)[number];

/** Full payload: every key is always present so GTM never reads a stale value from a previous push. */
export type AnalyticsPayload = {
  event: typeof ANALYTICS_EVENT;
  event_name: EventName;
  schema_version: 1;
  business_line: BusinessLine;
  service: Service;
  page_type: PageType;
  page_location: string;
  page_title: string;
  page_referrer: string | null;
  form_id: FormId | null;
  cta_location: CtaLocation | null;
  destination_type: DestinationType | null;
  case_id: CaseId | null;
};

export type EventExtras = {
  /** overrides the route-derived service (e.g. the service chosen in the diagnosis form) */
  service?: Service;
  form_id?: FormId;
  cta_location?: CtaLocation;
  destination_type?: DestinationType;
  case_id?: CaseId;
};

const oneOf = <T extends string>(list: readonly T[], v: unknown): v is T =>
  typeof v === "string" && (list as readonly string[]).includes(v);

// ---------- Route policy ----------

const PRIVATE_PREFIXES = ["/crm", "/auth", "/api", "/admin", "/seo-dashboard"];

export function isTrackablePath(pathname: string): boolean {
  const p = normalisePath(pathname);
  return !PRIVATE_PREFIXES.some((pre) => p === pre || p.startsWith(`${pre}/`));
}

function normalisePath(pathname: string): string {
  let p = ((pathname || "/").split("#")[0] ?? "").split("?")[0] ?? "/";
  if (!p.startsWith("/")) p = `/${p}`;
  p = p.replace(/\/{2,}/g, "/");
  if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
  return p.toLowerCase();
}

export function pageTypeFor(pathname: string): PageType {
  const p = normalisePath(pathname);
  if (p === "/") return "home";
  if (p === "/blog") return "knowledge_index";
  if (p.startsWith("/blog/")) return "article";
  if (p === "/casos-exito") return "case_studies";
  if (p === "/agenda-consultoria") return "contact";
  if (p.startsWith("/legal/")) return "legal";
  if (p === "/faqs") return "faq";
  if (["/quienes-somos", "/sobre-nosotros", "/programa-agencias", "/zonas-exclusivas"].includes(p)) return "company";
  if (p.startsWith("/servicios") || p === "/mas-servicios") return "service";
  if (/^\/(agencia-|consultor-|posicionamiento-|donde-|cual-|como-|seo-local-vs)/.test(p)) return "landing";
  return "other";
}

export function serviceFor(pathname: string): Service {
  const p = normalisePath(pathname);
  if (/geo|visibilidad-ia|chatgpt|-ia-espana/.test(p) && !/seo-local-ia|seo-web-ia|seo-ecommerce-ia/.test(p)) return "ai_visibility";
  if (/seo-local|google-maps|cerca|resenas|merchant/.test(p)) return "google_maps";
  if (/creacion-web|seo-web|posicionamiento-web|ecommerce|shopify/.test(p)) return "web";
  if (p.startsWith("/servicios")) return "other";
  return "unknown";
}

// ---------- URL sanitising ----------

// Explicit browser-safe campaign registry. Update it BEFORE launching a new campaign; any value not
// listed here is dropped. utm_term is never forwarded (free text). This is an allowlist, not PII detection.
export const UTM_REGISTRY = {
  utm_source: ["linkedin", "instagram", "newsletter", "google", "bing", "partner"],
  utm_medium: ["organic_social", "email", "referral", "cpc"],
  utm_campaign: ["caso_herrolex", "caso_la_sagra", "diagnostico_seo_local", "diagnostico_ia", "alquiler_webs", "conocimiento_newsletter"],
  utm_content: ["documento_01", "video_01", "post_01", "cta_diagnostico"],
} as const;
const UTM_KEYS = Object.keys(UTM_REGISTRY) as (keyof typeof UTM_REGISTRY)[];

export function hostMatches(hostname: string, domain: string): boolean {
  const h = hostname.toLowerCase();
  return h === domain || h.endsWith(`.${domain}`);
}

/**
 * Canonical page URL: site origin + path + only UTM values present in UTM_REGISTRY.
 * Hash, q, utm_term, gclid/gbraid/wbraid and every other param are dropped.
 */
export function sanitizePageUrl(href: string, opts: { allowUtm: boolean }): string | null {
  let u: URL;
  try {
    u = new URL(href, SITE_ORIGIN);
  } catch {
    return null;
  }
  const path = normalisePath(u.pathname);
  const params = new URLSearchParams();
  if (opts.allowUtm) {
    for (const k of UTM_KEYS) {
      const v = u.searchParams.get(k);
      if (v && (UTM_REGISTRY[k] as readonly string[]).includes(v)) params.set(k, v);
    }
  }
  const qs = params.toString();
  return `${SITE_ORIGIN}${path}${qs ? `?${qs}` : ""}`;
}

/** External referrers are reduced to their origin; internal ones to the canonical path without query. */
export function sanitizeReferrer(ref: string | null | undefined, currentHost: string): string | null {
  if (!ref) return null;
  try {
    const u = new URL(ref);
    if (u.protocol !== "https:" && u.protocol !== "http:") return null;
    if (u.host === currentHost || hostMatches(u.hostname, "agenciaseoevolution.com")) {
      return sanitizePageUrl(u.pathname, { allowUtm: false });
    }
    return u.origin;
  } catch {
    return null;
  }
}

// ---------- Destination classification (never emits the destination URL itself) ----------

export function classifyHref(href: string | null | undefined): DestinationType | null {
  if (!href) return null;
  const h = href.trim().toLowerCase();
  if (h.startsWith("tel:")) return "phone";
  if (h.startsWith("mailto:")) return "email";
  try {
    const u = new URL(h, SITE_ORIGIN);
    if (u.hostname === "wa.me" || hostMatches(u.hostname, "whatsapp.com")) return "whatsapp";
    if (hostMatches(u.hostname, "calendly.com")) return "calendly";
    if (u.origin === SITE_ORIGIN && normalisePath(u.pathname) === "/agenda-consultoria") return "diagnosis";
  } catch {
    return null;
  }
  return null;
}

// ---------- Event building ----------

export type PageContext = { href: string; title: string; referrer: string | null };

export function buildPayload(
  name: EventName,
  page: PageContext,
  extras: EventExtras = {},
  opts: { allowUtm: boolean } = { allowUtm: false },
): AnalyticsPayload | null {
  if (!oneOf(EVENT_NAMES, name)) return null;
  let path: string;
  try {
    path = new URL(page.href, SITE_ORIGIN).pathname;
  } catch {
    return null;
  }
  if (!isTrackablePath(path)) return null;
  const location = sanitizePageUrl(page.href, opts);
  if (!location) return null;
  return {
    event: ANALYTICS_EVENT,
    event_name: name,
    schema_version: ANALYTICS_SCHEMA_VERSION,
    business_line: "agency",
    service: oneOf(SERVICES, extras.service) ? extras.service : serviceFor(path),
    page_type: pageTypeFor(path),
    page_location: location,
    page_title: sanitizeTitle(page.title),
    page_referrer: page.referrer,
    form_id: oneOf(FORM_IDS, extras.form_id) ? extras.form_id : null,
    cta_location: oneOf(CTA_LOCATIONS, extras.cta_location) ? extras.cta_location : null,
    destination_type: oneOf(DESTINATION_TYPES, extras.destination_type) ? extras.destination_type : null,
    case_id: oneOf(CASE_IDS, extras.case_id) ? extras.case_id : null,
  };
}

/** Site page titles only; anything that looks like personal data falls back to the brand. */
export function sanitizeTitle(title: string | null | undefined): string {
  const t = (title || "").replace(/\s+/g, " ").trim().slice(0, 150);
  if (!t || /@|https?:\/\/|\+?\d[\d\s]{7,}/.test(t)) return SITE_TITLE;
  return t;
}

// ---------- Tracker (consent + host gate, dedup) ----------

export type TrackerDeps = {
  /** true only when analytics consent is granted right now */
  analyticsGranted: () => boolean;
  /** true only on the production host */
  isProduction: () => boolean;
  /** production transport (dataLayer.push) */
  send: (p: AnalyticsPayload) => void;
  /** preview double; receives payloads instead of the network transport */
  qaSink?: ((p: AnalyticsPayload) => void) | undefined;
  page: () => PageContext;
};

export function createTracker(deps: TrackerDeps) {
  let lastPageKey: string | null = null;
  const once = new Set<string>();

  function emit(name: EventName, extras: EventExtras = {}, page = deps.page()): AnalyticsPayload | null {
    if (!deps.analyticsGranted()) return null;
    const p = buildPayload(name, page, extras, { allowUtm: true });
    if (!p) return null;
    if (deps.isProduction()) deps.send(p);
    else if (deps.qaSink) deps.qaSink(p);
    else return null;
    return p;
  }

  return {
    track: emit,
    /** One page_view per real page (path + allowlisted query); hash/scroll/modals never count. */
    pageView(page = deps.page()): AnalyticsPayload | null {
      const key = sanitizePageUrl(page.href, { allowUtm: true });
      if (!key || key === lastPageKey) return null;
      const p = emit("page_view", {}, page);
      if (p) lastPageKey = key;
      return p;
    },
    /** Emits at most once per key for this visit (e.g. case study views, Calendly confirmation ids). */
    trackOnce(key: string, name: EventName, extras: EventExtras = {}): AnalyticsPayload | null {
      if (once.has(key)) return null;
      const p = emit(name, extras);
      if (p) once.add(key);
      return p;
    },
    lastPageLocation: () => lastPageKey,
  };
}

// ---------- Calendly ----------

export const CALENDLY_ORIGIN = "https://calendly.com";

/** Returns an opaque internal dedup key for a genuine scheduled booking message, else null. */
export function calendlyScheduledKey(
  ev: { origin: string; data: unknown; source: unknown },
  iframeWindows: unknown[],
): string | null {
  if (ev.origin !== CALENDLY_ORIGIN) return null;
  if (!ev.source || !iframeWindows.includes(ev.source)) return null;
  const d = ev.data as { event?: unknown; payload?: unknown } | null;
  if (!d || typeof d !== "object" || d.event !== "calendly.event_scheduled") return null;
  const payload = d.payload as { event?: { uri?: unknown }; invitee?: { uri?: unknown } } | null;
  if (!payload || typeof payload !== "object") return null;
  const uri = payload.invitee?.uri ?? payload.event?.uri;
  if (typeof uri !== "string" || !uri.startsWith("https://api.calendly.com/")) return null;
  return uri;
}

/** Maps the diagnosis form's chosen service to the analytics enum; nothing else from the form is used. */
export function serviceFromDiagnosis(servicio: unknown): Service {
  switch (servicio) {
    case "google-maps-seo-local": return "google_maps";
    case "seo-visibilidad-ia": return "ai_visibility";
    case "web": return "web";
    case "otro": return "other";
    default: return "unknown";
  }
}
